import { buildAuthHeaders } from './auth';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export function resolveApiPath(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized === API_BASE) return API_BASE;
  return normalized.startsWith(`${API_BASE}/`) ? normalized : `${API_BASE}${normalized}`;
}

export async function apiFetch<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const baseHeaders: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  if (options.body !== undefined) {
    baseHeaders['Content-Type'] = 'application/json';
  }
  const headers = buildAuthHeaders(baseHeaders);

  const res = await fetch(resolveApiPath(path), {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText, error: res.statusText }));
    throw new Error(body.message || body.error || `API error ${res.status}`);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export function get<T = unknown>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: 'GET' });
}

export function post<T = unknown>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  });
}

export function put<T = unknown>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export function patch<T = unknown>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export function del<T = unknown>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: 'DELETE' });
}
